/**
 * ERPNext Orders API Service
 * Fetch user's order history and details
 */

const ERPNEXT_URL = import.meta.env.VITE_ERPNEXT_URL || '';

export interface OrderItem {
  item_code: string;
  item_name: string;
  qty: number;
  rate: number;
  amount: number;
  image?: string;
}

export interface Order {
  name: string; // Sales Order ID
  customer: string;
  customer_name: string;
  transaction_date: string;
  delivery_date?: string;
  status: string; // Draft, To Deliver and Bill, To Bill, To Deliver, Completed, Cancelled
  custom_order_status?: string; // Custom status field
  grand_total: number;
  currency: string;
  items?: OrderItem[];
  shipping_address_name?: string;
  billing_address_name?: string;
  docstatus: number; // 0 = Draft, 1 = Submitted, 2 = Cancelled
}

/**
 * Get all orders for current user
 */
export async function getOrders(): Promise<Order[]> {
  try {
    const response = await fetch(
      `${ERPNEXT_URL}/api/method/webshop.webshop.shopping_cart.cart.get_customer_orders`,
      {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
        },
      }
    );

    if (!response.ok) {
      // If webshop method doesn't exist, return empty array
      console.warn('Unable to fetch orders - endpoint may not exist');
      return [];
    }

    const data = await response.json();
    return data.message || [];
  } catch (error) {
    console.error('Get orders error:', error);
    // Don't throw - just return empty array for now
    return [];
  }
}

/**
 * Get specific order details with items
 * Uses whitelisted method that validates customer ownership
 */
export async function getOrderDetails(orderId: string): Promise<Order> {
  try {
    const response = await fetch(
      `${ERPNEXT_URL}/api/method/webshop.webshop.shopping_cart.cart.get_order_details`,
      {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ order_name: orderId }),
      }
    );

    if (!response.ok) {
      throw new Error('Failed to fetch order details');
    }

    const data = await response.json();
    return data.message;
  } catch (error) {
    console.error('Get order details error:', error);
    throw error;
  }
}

/**
 * Get user-friendly status label
 */
export function getOrderStatusLabel(order: Order): string {
  if (order.docstatus === 0) return 'Draft';
  if (order.docstatus === 2) return 'Cancelled';

  // Use custom order status if available
  if (order.custom_order_status) {
    return order.custom_order_status;
  }

  // Fallback to default ERPNext statuses
  switch (order.status) {
    case 'To Deliver and Bill':
      return 'Processing';
    case 'To Deliver':
      return 'Awaiting Shipment';
    case 'To Bill':
      return 'Awaiting Payment';
    case 'Completed':
      return 'Delivered';
    default:
      return order.status;
  }
}

/**
 * Get status color class
 */
export function getOrderStatusColor(order: Order): string {
  if (order.docstatus === 0) return 'bg-gray-100 text-gray-700';
  if (order.docstatus === 2) return 'bg-red-100 text-red-700';

  // Use custom status colors if available
  const customStatus = order.custom_order_status;
  if (customStatus) {
    switch (customStatus) {
      case 'Order Received':
      case 'Processing Payment':
        return 'bg-blue-100 text-blue-700';
      case 'Assembling':
      case 'Quality Check':
        return 'bg-amber-100 text-amber-700';
      case 'Assembled':
      case 'Ready to Ship':
        return 'bg-purple-100 text-purple-700';
      case 'Shipped':
      case 'Out for Delivery':
        return 'bg-indigo-100 text-indigo-700';
      case 'Delivered':
      case 'Completed':
        return 'bg-green-100 text-green-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  }

  // Fallback to default ERPNext status colors
  switch (order.status) {
    case 'Completed':
      return 'bg-green-100 text-green-700';
    case 'To Deliver and Bill':
    case 'To Deliver':
      return 'bg-blue-100 text-blue-700';
    case 'To Bill':
      return 'bg-amber-100 text-amber-700';
    default:
      return 'bg-gray-100 text-gray-700';
  }
}
